export type WalkPreset = {
    id: string;
    name: string;
    heightMeters: number;
    builtin: boolean;
};

export const DEFAULT_PRESETS: WalkPreset[] = [
    { id: 'standing', name: 'Standing Adult', heightMeters: 1.7, builtin: true },
    { id: 'wheelchair', name: 'Wheelchair', heightMeters: 1.17, builtin: true },
    { id: 'child', name: 'Child', heightMeters: 1.07, builtin: true },
    { id: 'suv', name: 'SUV Driver', heightMeters: 1.42, builtin: true },
    { id: 'truck', name: 'Truck Cab', heightMeters: 2.13, builtin: true }
];

const sanitizeName = (name: unknown): string => {
    if (typeof name !== 'string') return '';
    return name.trim();
};

const sanitizeHeight = (heightMeters: unknown): number | null => {
    if (typeof heightMeters !== 'number' || !Number.isFinite(heightMeters)) return null;
    // Clamp to something sane for a "camera height".
    return Math.max(0.1, Math.min(10, heightMeters));
};

const generateId = (): string => {
    // Avoid importing a UUID dep; this is persisted only within .nsite.
    return `custom_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
};

export class WalkPresetManager {
    private customPresets: WalkPreset[] = [];

    getPreset(id: string): WalkPreset | undefined {
        return this.getAll().find(p => p.id === id);
    }

    getAll(): WalkPreset[] {
        return [...DEFAULT_PRESETS, ...this.customPresets];
    }

    addCustomPreset(name: string, heightMeters: number): WalkPreset {
        const safeName = sanitizeName(name) || 'Custom Preset';
        const safeHeight = sanitizeHeight(heightMeters) ?? 1.7;

        const preset: WalkPreset = {
            id: generateId(),
            name: safeName,
            heightMeters: safeHeight,
            builtin: false
        };

        this.customPresets.push(preset);
        return preset;
    }

    removeCustomPreset(id: string): void {
        this.customPresets = this.customPresets.filter(p => p.id !== id);
    }

    serialize(): Array<{ id: string; name: string; heightMeters: number }> {
        // Persist ONLY custom presets.
        return this.customPresets.map(p => ({ id: p.id, name: p.name, heightMeters: p.heightMeters }));
    }

    deserialize(data: unknown): void {
        if (!Array.isArray(data)) {
            this.customPresets = [];
            return;
        }

        const custom: WalkPreset[] = [];
        for (const item of data) {
            const obj: any = item;
            const id = typeof obj?.id === 'string' ? obj.id : generateId();
            const name = sanitizeName(obj?.name) || 'Custom Preset';
            const height = sanitizeHeight(obj?.heightMeters);
            if (height === null) continue;
            custom.push({ id, name, heightMeters: height, builtin: false });
        }

        this.customPresets = custom;
    }
}
